/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useCallback, useState, useMemo } from 'react';
import { VStack, Box, Grid, Separator } from 'chakra-v3';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams, getTypedComponent } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { Scrollbar } from '../../../../../components/Scrollbar';
import { Button } from '../../../../../components/Index';
import { ChatBubble } from '../../shared/ChatBubble';
import CardShell from '../../../../../components/CardShell';
import { useNext } from '../../../hooks/useNext';
import { UserPRPTextComparisonComponent } from '@karya/core';

export const UserPRPTextComparison: React.FC<ComponentRendererProps> = ({ node, isCurrent, controller }) => {
  const params = getTypedParams<UserPRPTextComparisonComponent>(node);
  const component = getTypedComponent<UserPRPTextComparisonComponent>(node);

  const modelAName = params.model_a_name ?? 'Model A';
  const modelAResponse = params.model_a_response ?? '';
  const modelBName = params.model_b_name ?? 'Model B';
  const modelBResponse = params.model_b_response ?? '';
  const randomizeOrder = params.randomize_order ?? false;
  const anonymizeModelNames = params.anonymize_model_names ?? false;

  const [randomized] = useState(() => {
    if (randomizeOrder) {
      return Math.random() > 0.5;
    }
    return false;
  });

  const { displayNameA, displayNameB, responseA, responseB, actualModelNameA, actualModelNameB } = useMemo(() => {
    let nameA: string = modelAName;
    let nameB: string = modelBName;
    let respA = modelAResponse;
    let respB = modelBResponse;

    // After this block, column A/B are fully determined (including randomization)
    const actualA = randomized ? modelBName : modelAName;
    const actualB = randomized ? modelAName : modelBName;

    if (randomized) {
      [nameA, nameB] = [nameB, nameA];
      [respA, respB] = [respB, respA];
    }

    if (anonymizeModelNames) {
      nameA = 'Response A';
      nameB = 'Response B';
    }

    return {
      displayNameA: nameA,
      displayNameB: nameB,
      responseA: respA,
      responseB: respB,
      actualModelNameA: actualA,
      actualModelNameB: actualB,
    };
  }, [modelAName, modelBName, modelAResponse, modelBResponse, randomized, anonymizeModelNames]);

  const safeTeardownFn = useCallback(
    (next: () => void) => {
      if (!params.selected_model) {
        return;
      }

      // Only call next() when validation passes and output is written
      next();
    },
    [params.selected_model],
  );

  useNext({
    controller,
    teardownFn: safeTeardownFn,
    strategy: 'MANUAL',
    isCurrent,
  });

  const onCardClick = (side: 'A' | 'B') => {
    const actualModelName = side === 'A' ? actualModelNameA : actualModelNameB;
    controller.writeOutput(component.selected_model, actualModelName);
  };

  const ResponseColumn = ({
    label,
    response,
    isSelected,
    onClick,
  }: {
    label: string;
    response: string;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <VStack alignItems="flex-start" gap="20px" w="100%" h="100%" p={{ base: '20px', md: '32px' }}>
      <Text variant="labelBold" color="text.primary">
        {label}
      </Text>
      <Scrollbar width="100%" maxHeight="260px" flex="1">
        <Text variant="bodyExtended" color="text.primary">
          {response}
        </Text>
      </Scrollbar>
      <Button
        variant={isSelected ? 'selected' : 'secondary'}
        display="inline-flex"
        alignItems="flex-start"
        w="fit-content"
        onClick={onClick}
        disabled={!isCurrent}
        _disabled={{
          opacity: 0.5,
          cursor: 'not-allowed',
        }}
      >
        I prefer {label}
      </Button>
    </VStack>
  );

  return (
    <Box
      w="100%"
      display="flex"
      justifyContent="center"
      px={{ base: '16px', md: '24px' }}
      py="24px"
      position="relative"
    >
      <CardShell>
        <ChatBubble
          containerProps={{
            w: '100%',
            position: 'relative',
            zIndex: 1,
          }}
          innerPadding={{ base: '12px', md: '16px' }}
        >
          <Box bg="brand.teal" color="text.primary" position="relative" overflow="hidden" zIndex={1}>
            <VStack gap="24px" w="100%">
              <Grid
                templateColumns="1fr auto 1fr"
                gap={{ base: '24px', md: '32px' }}
                position="relative"
                zIndex={1}
                w="100%"
                alignItems="stretch"
              >
                <ResponseColumn
                  label={displayNameA}
                  response={responseA}
                  isSelected={params.selected_model === actualModelNameA}
                  onClick={() => onCardClick('A')}
                />
                <Separator orientation="vertical" height="85%" alignSelf="center" borderColor="connector.default" />
                <ResponseColumn
                  label={displayNameB}
                  response={responseB}
                  isSelected={params.selected_model === actualModelNameB}
                  onClick={() => onCardClick('B')}
                />
              </Grid>
            </VStack>
          </Box>
        </ChatBubble>
      </CardShell>
    </Box>
  );
};
